import React from 'react';
import { ClientLead, clientApiService } from '../services/client.api';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface ClientLeadsTableProps {
  leads: ClientLead[];
  onUpdate?: (leadId: number, updates: any) => void;
  onRefresh?: () => void;
  loading?: boolean;
  userId?: number; // ID текущего пользователя для проверки прав
}

// ID пользователей, для которых отключены ограничения на изменение статуса
const UNRESTRICTED_USER_IDS = [446, 150, 97];

export const ClientLeadsTable: React.FC<ClientLeadsTableProps> = ({
  leads,
  onUpdate,
  onRefresh,
  loading = false,
  userId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Проверяем, является ли текущий пользователь "неограниченным"
  const isUnrestrictedUser = userId !== undefined && UNRESTRICTED_USER_IDS.includes(userId);

  const updateLeadField = async (leadId: number, field: string, value: any) => {
    try {
      // Обновляем поле лида через клиентский API
      if (field === 'status') {
        // Используем общий метод updateByClient для статуса
        await clientApiService.updateByClient(leadId, { status: value });
      } else if (field === 'disput_comment') {
        // Используем общий метод updateByClient для комментария диспута
        await clientApiService.updateByClient(leadId, { disputComment: value });
      } else if (field === 'client_manager_name') {
        // Используем общий метод updateByClient для имени менеджера клиента
        await clientApiService.updateByClient(leadId, { clientManagerName: value });
      } else if (field === 'comment') {
        await clientApiService.saveLeadComment(leadId, value);
      } else {
        // Для других полей используем общий метод обновления
        await clientApiService.updateByClient(leadId, { [field]: value });
      }
      
      // Вызываем фоновой рефетч только таблицы без глобального лоадера
      if (onRefresh) {
        void onRefresh();
      }
    } catch (error) {
      console.error(`Ошибка при обновлении поля ${field}:`, error);
      console.error(`Ошибка при обновлении поля ${field}:`, error);
    }
  };

  const statusOptions = [
    { value: 'UNPROCESSED', label: 'Необработанный' },
    { value: 'AT_WORK', label: 'В работе' },
    { value: 'COMPLETED', label: 'Завершен' },
    { value: 'NON_CALL', label: 'Недозвон' },
    { value: 'INVALID', label: 'Невалидный лид' },
    { value: 'BONUS', label: 'Бонусный' },
  ];

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
  }

  const renderStatusSelect = (lead: ClientLead) => (
    <select
      value={lead.status || 'UNPROCESSED'}
      onChange={(e) => {
        const newStatus = e.target.value;
        const isLocked = lead.sent_at ? (Date.now() - new Date(lead.sent_at).getTime()) > (24 * 60 * 60 * 1000) : false;
        const isBonus = !!lead.bonus || lead.status === 'BONUS';
        
        if (!isUnrestrictedUser) {
          if (isBonus) return;
          if (isLocked && newStatus !== 'COMPLETED') return;
        }
        if (onUpdate) onUpdate(lead.id, { status: newStatus });
        updateLeadField(lead.id, 'status', newStatus);
      }}
      style={{ 
        width: '100%', 
        padding: '6px', 
        fontSize: '12px', 
        border: '1px solid #ccc', 
        borderRadius: '4px',
        backgroundColor: 'white',
        color: 'black'
      }}
      disabled={!isUnrestrictedUser && (!!lead.bonus || lead.status === 'BONUS')}
    >
      {statusOptions.map(option => {
        const isLocked = lead.sent_at ? (Date.now() - new Date(lead.sent_at).getTime()) > (24 * 60 * 60 * 1000) : false;
        const isBonus = !!lead.bonus || lead.status === 'BONUS';
        const disabled = !isUnrestrictedUser && ((isBonus) || (isLocked && option.value !== 'COMPLETED'));
        return (
          <option key={option.value} value={option.value} style={{ color: 'black' }} disabled={disabled}>
            {option.label}
          </option>
        );
      })}
    </select>
  );

  return (
    <div style={{ width: '100%' }}>
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {leads.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: 'center' }}>Лиды не найдены</Typography>
          ) : (
            leads.map(lead => (
              <Box
                key={lead.id}
                sx={{
                  p: 2,
                  border: lead.bonus ? '1px solid #4caf50' : '1px solid #ddd',
                  borderRadius: 2,
                  backgroundColor: lead.bonus ? '#e8f5e9' : 'white',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 'bold' }}>
                      ID: {lead.id}
                    </Typography>
                    {lead.bonus && (
                      <span style={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                      }}>БОНУС</span>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(lead.sent_at || lead.created_at).toLocaleString('ru-RU')}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 'bold' }}>
                    {lead.phone}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Статус:</Typography>
                    {renderStatusSelect(lead)}
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Менеджер:</Typography>
                    <input
                      type="text"
                      value={lead.client_manager_name || ''}
                      onChange={(e) => onUpdate && onUpdate(lead.id, { client_manager_name: e.target.value })}
                      onBlur={(e) => updateLeadField(lead.id, 'client_manager_name', e.target.value)}
                      style={{ 
                        width: '100%', 
                        border: '1px solid #ccc', 
                        borderRadius: '4px',
                        fontSize: '12px',
                        padding: '6px',
                        outline: 'none'
                      }}
                      placeholder="Имя..."
                    />
                  </Box>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Примечание:</Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, p: 1, bgcolor: '#f9f9f9', borderRadius: 1, minHeight: 40 }}>
                    {lead.note || '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Комментарий BT:</Typography>
                  <Typography variant="body2" sx={{ fontSize: 12, p: 1, bgcolor: '#f9f9f9', borderRadius: 1, minHeight: 40 }}>
                    {lead.comment || '-'}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary">Диспут:</Typography>
                  <textarea
                    value={lead.disput_comment || ''}
                    onChange={(e) => onUpdate && onUpdate(lead.id, { disput_comment: e.target.value })}
                    onBlur={(e) => updateLeadField(lead.id, 'disput_comment', e.target.value)}
                    style={{
                      width: '100%',
                      minHeight: 60,
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      resize: 'none',
                      fontSize: '12px',
                      padding: '6px',
                      outline: 'none'
                    }}
                    placeholder="Ваш комментарий..."
                  />
                </Box>
              </Box>
            ))
          )}
        </Box>
      ) : (
        <div style={{ overflowX: 'auto', width: '100%' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 0, minWidth: '1200px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f5f5f5' }}>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>ID</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Телефон</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Дата/время</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Примечание</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Комментарий</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Клиент</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Менеджер BT</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Статус</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '1%' }}>Менеджер</th>
                <th style={{ padding: 8, border: '1px solid #ddd', textAlign: 'left', width: '35%' }}>Диспут</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: 16, textAlign: 'center' }}>Лиды не найдены</td>
                </tr>
              ) : (
                leads.map(lead => (
                  <tr key={lead.id} style={{ backgroundColor: lead.bonus ? '#e8f5e9' : undefined }}>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      {lead.id}
                      {lead.bonus && (
                        <span style={{
                          backgroundColor: '#4caf50',
                          color: 'white',
                          padding: '1px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          marginLeft: '6px',
                        }}>БОНУС</span>
                      )}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>{lead.phone}</td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      {new Date(lead.sent_at || lead.created_at).toLocaleString('ru-RU')}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      {lead.note ? (
                        <div style={{ maxWidth: 200, wordWrap: 'break-word', fontSize: '12px', lineHeight: '1.2' }}>
                          {lead.note}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: 10 }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd', height: '100px' }}>
                      {lead.comment ? (
                        <div style={{ maxWidth: 200, wordWrap: 'break-word', fontSize: '12px', lineHeight: '1.2' }}>
                          {lead.comment}
                        </div>
                      ) : (
                        <span style={{ color: '#999', fontSize: 10 }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      <span style={{ fontSize: '12px' }}>
                        {lead.customer_name || 'Без имени'}
                      </span>
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      {lead.manager_name ? (
                        <span style={{ fontSize: '12px' }}>{lead.manager_name}</span>
                      ) : (
                        <span style={{ color: '#999', fontSize: 10 }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd' }}>
                      {renderStatusSelect(lead)}
                    </td>
                    <td style={{ fontSize: '12px', padding: 8, border: '1px solid #ddd' }}>
                      <input
                        type="text"
                        value={lead.client_manager_name || ''}
                        onChange={(e) => {
                          const newManagerName = e.target.value;
                          if (onUpdate) {
                            onUpdate(lead.id, { client_manager_name: newManagerName });
                          }
                        }}
                        onBlur={(e) => updateLeadField(lead.id, 'client_manager_name', e.target.value)}
                        style={{ 
                          width: '100%', 
                          border: '1px solid #ccc', 
                          backgroundColor: 'white',
                          fontSize: '12px',
                          padding: '4px',
                          outline: 'none'
                        }}
                        placeholder="Имя менеджера..."
                      />
                    </td>
                    <td style={{ padding: 8, border: '1px solid #ddd', minWidth: '400px' }}>
                      <textarea
                          value={lead.disput_comment || ''}
                          onChange={(e) => {
                            const newDisputeComment = e.target.value;
                            if (onUpdate) {
                              onUpdate(lead.id, { disput_comment: newDisputeComment });
                            }
                          }}
                          onBlur={(e) => updateLeadField(lead.id, 'disput_comment', e.target.value)}
                          style={{
                            width: '100%',
                            minHeight: '120px',
                            border: '1px solid #ccc',
                            resize: 'vertical',
                            backgroundColor: 'white',
                            fontFamily: 'inherit',
                            fontSize: '13px',
                            lineHeight: '1.4',
                            padding: '6px',
                            outline: 'none'
                          }}
                          placeholder="Комментарий диспута..."
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};